import type { CSSProperties } from 'react';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from './Store.ts';
import {
  DEFAULT_TABLE_HEADER_ROW_BACKGROUND_COLOR,
  DEFAULT_TABLE_FOOTER_ROW_BACKGROUND_COLOR,
  DEFAULT_TABLE_SUBTOTAL_ROW_BACKGROUND_COLOR,
  DEFAULT_TABLE_TOTAL_ROW_BACKGROUND_COLOR,
  DEFAULT_TABLE_BODY_ROW_BACKGROUND_COLOR,
} from '../../shared/documentColorDefaults';

type CustomCSSProperties = {
  '--table-row-default-background-color': string;
};

export type TableRowStyleType = CustomCSSProperties & {
  [K in keyof CSSProperties]: Exclude<CSSProperties[K], undefined>;
};

export enum TableRowDefaultClassNamesEnum {
  BODY = 'py-body-row',
  HEADER = 'py-header-row',
  FOOTER = 'py-footer-row',
  TAX = 'py-subtotal-row',
  DISCOUNT = 'py-subtotal-row',
  SUBTOTAL = 'py-subtotal-row',
  TOTAL = 'py-total-row',
}

export enum TableRowDefaultSelectorsEnum {
  BODY = `.${TableRowDefaultClassNamesEnum.BODY}`,
  HEADER = `.${TableRowDefaultClassNamesEnum.HEADER}`,
  FOOTER = `.${TableRowDefaultClassNamesEnum.FOOTER}`,
  SUBTOTAL = `.${TableRowDefaultClassNamesEnum.SUBTOTAL}`,
  TOTAL = `.${TableRowDefaultClassNamesEnum.TOTAL}`,
}

export type TableRowDefaultTagSelectors = `${TableRowDefaultSelectorsEnum}`;

export type TableRowStyleState = Record<TableRowDefaultTagSelectors, TableRowStyleType>;

export const tableRowDefaultStyle: TableRowStyleState = {
  [TableRowDefaultSelectorsEnum.BODY]: {
    '--table-row-default-background-color': DEFAULT_TABLE_BODY_ROW_BACKGROUND_COLOR,
  },
  [TableRowDefaultSelectorsEnum.HEADER]: {
    '--table-row-default-background-color': DEFAULT_TABLE_HEADER_ROW_BACKGROUND_COLOR,
  },
  [TableRowDefaultSelectorsEnum.FOOTER]: {
    '--table-row-default-background-color': DEFAULT_TABLE_FOOTER_ROW_BACKGROUND_COLOR,
  },
  [TableRowDefaultSelectorsEnum.SUBTOTAL]: {
    '--table-row-default-background-color': DEFAULT_TABLE_SUBTOTAL_ROW_BACKGROUND_COLOR,
  },
  [TableRowDefaultSelectorsEnum.TOTAL]: {
    '--table-row-default-background-color': DEFAULT_TABLE_TOTAL_ROW_BACKGROUND_COLOR,
  },
};

export type UpdateTableRowStylingPayloadType = {
  type: TableRowDefaultTagSelectors;
  style: Partial<TableRowStyleType>;
};

const tableRowDefaultStyleSlice = createSlice({
  name: 'editor-table-row-default-styling',
  initialState: tableRowDefaultStyle,
  reducers: {
    setTableRowStyle: (state, action: PayloadAction<TableRowStyleState>) => {
      return {
        ...state,
        ...action.payload,
      };
    },
  },
});
export const { setTableRowStyle } = tableRowDefaultStyleSlice.actions;
export const tableRowDefaultStyleReducer = tableRowDefaultStyleSlice.reducer;
export const selectTableRowStyles = (state: RootState) => state.tableRowDefaultStyleReducer;
